
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface LocationOverride {
  location_id: number;
  location_name: string | null;
  screen_count_override: number | null;
  custom_price_per_device: number | null;
  region: string | null;
  screen_type: string | null;
  media_kind: string | null;
  is_available: boolean;
  maintenance_status: string | null;
  maintenance_notes: string | null;
  extra_details: {
    description?: string;
    screenDimensions?: string;
    resolution?: string;
    lat?: number;
    lng?: number;
    gender?: string;
    ageRange?: string;
    segment?: string;
    orientation?: string;
    locationType?: string;
  } | null;
}

// Merges rows ordered by updated_at ASC so later values win.
// Every row is a valid override — no filtering guard.
const buildMap = (data: any[]): Map<number, LocationOverride> => {
  const map = new Map<number, LocationOverride>();
  (data || []).forEach((row) => {
    const existing = map.get(row.location_id);
    if (existing) {
      map.set(row.location_id, {
        ...existing,
        ...Object.fromEntries(
          Object.entries(row).filter(([, v]) => v !== null && v !== undefined)
        ),
        extra_details: {
          ...(existing.extra_details ?? {}),
          ...(row.extra_details as LocationOverride['extra_details'] ?? {}),
        },
      } as LocationOverride);
    } else {
      map.set(row.location_id, row as unknown as LocationOverride);
    }
  });
  return map;
};

// Merge a single updated row into an existing Map (avoids full re-fetch on Realtime events).
const mergeRowIntoMap = (
  prev: Map<number, LocationOverride>,
  row: any
): Map<number, LocationOverride> => {
  const next = new Map(prev);
  const existing = next.get(row.location_id);
  if (existing) {
    next.set(row.location_id, {
      ...existing,
      ...Object.fromEntries(
        Object.entries(row).filter(([, v]) => v !== null && v !== undefined)
      ),
      extra_details: {
        ...(existing.extra_details ?? {}),
        ...(row.extra_details as LocationOverride['extra_details'] ?? {}),
      },
    } as LocationOverride);
  } else {
    next.set(row.location_id, row as unknown as LocationOverride);
  }
  return next;
};

const RETRY_DELAYS = [1000, 2000, 4000]; // exponential backoff

export const useLocationOverrides = () => {
  const [overrides, setOverrides] = useState<Map<number, LocationOverride>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Unique channel name per hook instance to avoid collision when multiple DeviceMaps mount.
  const channelNameRef = useRef(`mol_map_${Math.random().toString(36).slice(2)}`);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const fetchOverrides = useCallback(async (isRetry = false): Promise<void> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('media_owner_locations')
        .select(
          'location_id, location_name, screen_count_override, custom_price_per_device, extra_details, region, screen_type, media_kind, is_available, maintenance_status, maintenance_notes'
        )
        .order('updated_at', { ascending: true });

      if (fetchError) throw fetchError;

      if (!isMountedRef.current) return;
      setOverrides(buildMap(data || []));
      setError(null);
      retryCountRef.current = 0;
    } catch (err: any) {
      if (!isMountedRef.current) return;

      const attempt = retryCountRef.current;
      if (attempt < RETRY_DELAYS.length) {
        retryCountRef.current += 1;
        const delay = RETRY_DELAYS[attempt];
        retryTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) fetchOverrides(true);
        }, delay);
      } else {
        // All retries exhausted — surface the error
        setError('Failed to load location data. Please refresh the page.');
        retryCountRef.current = 0;
      }
    } finally {
      if (isMountedRef.current && !isRetry) {
        setLoading(false);
      } else if (isMountedRef.current && retryCountRef.current === 0) {
        // Successful retry — stop showing loading
        setLoading(false);
      }
    }
  }, []);

  const subscribeRealtime = useCallback(() => {
    // Tear down any existing channel before re-subscribing
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(channelNameRef.current)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'media_owner_locations' },
        (payload) => {
          if (!isMountedRef.current) return;

          // Optimistic merge: use the payload row directly — no round-trip needed.
          const newRow = payload.new as any;
          const oldRow = payload.old as any;

          if (payload.eventType === 'DELETE' && oldRow?.location_id != null) {
            // On delete, remove the entry from the map
            setOverrides(prev => {
              const next = new Map(prev);
              next.delete(oldRow.location_id);
              return next;
            });
            return;
          }

          if (newRow && typeof newRow.location_id === 'number') {
            setOverrides(prev => mergeRowIntoMap(prev, newRow));
          } else {
            // Incomplete payload — fall back to full re-fetch
            fetchOverrides();
          }
        }
      )
      .subscribe((status) => {
        if (!isMountedRef.current) return;
        // Reconnect if the channel drops
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          setTimeout(() => {
            if (isMountedRef.current) subscribeRealtime();
          }, 3000);
        }
      });

    channelRef.current = channel;
  }, [fetchOverrides]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchOverrides();
    subscribeRealtime();

    return () => {
      isMountedRef.current = false;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [fetchOverrides, subscribeRealtime]);

  return { overrides, loading, error };
};
